import { useProjectAudit } from "@/api/queries";
import type { AuditEvent } from "@/api/types";
import { Button } from "@/components/Button";
import { Panel } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { RunStateLabel, StatusLabel } from "@/components/StatusLabel";
import { useUiStore } from "@/store/ui";

const KIND_LABEL: Record<string, string> = {
  run: "运行",
  issue: "规则",
  decision: "人工决定",
};

const KIND_SYMBOL: Record<string, string> = {
  run: "◇",
  issue: "!",
  decision: "✔",
};

/**
 * 审计追溯（specs/03 §8 · `GET /projects/{id}/audit`）。
 *
 * 三类事实——运行（`capability_runs`）、规则（`validation_issues`）、
 * 人工决定（`review_records`）——**早已在库里**，这个端点只是把它们合成一条时间线。
 * 所以它是纯读模型：界面上不会有任何"写审计"的动作，历史也不会被本页改变。
 *
 * 计数是全量、列表只是最近 N 条，两者不要互相推导。
 *
 * @dataSource remote
 */
export function AuditView() {
  const projectId = useUiStore((state) => state.projectId);
  const audit = useProjectAudit(projectId);

  const counts = audit.data?.counts ?? {};
  const events = audit.data?.events ?? [];

  return (
    <div className="page-scroll">
      <div className="page-width">
        <RemoteState queries={[audit]} title="审计日志加载失败">
          <header className="page-heading">
            <div>
              <span className="eyebrow">运行 · 规则 · 人工决定</span>
              <h2>把三类事实按时间串起来，才看得见"谁在什么时候改了什么"</h2>
              <p>
                审计不另建表：能力运行、校验问题与人工决定本来就已经落库，这里只做只读聚合。
                多存一份"审计事件"就等于允许存在第二份真相，两份真相必然漂移。
              </p>
            </div>
            <Button
              size="small"
              variant="quiet"
              id="refresh-audit"
              disabled={audit.isFetching}
              onClick={() => void audit.refetch()}
            >
              {audit.isFetching ? "刷新中…" : "刷新"}
            </Button>
          </header>

          <section className="check-hero">
            <div className="check-score">
              <div className="check-score-number">
                {events.length}
                <small>最近事件 / 共 {sumCounts(counts)} 条</small>
              </div>
              <div>
                <h2>计数是全量，列表只截最近一段</h2>
                <p>
                  三类来源各取最近的记录后归并，因此某一类刷屏（例如刚跑完一次校验产生一屏规则事件）
                  不会把另外两类挤出列表。
                </p>
              </div>
            </div>
            <div className="check-stat-grid">
              <div className="check-stat">
                <strong>{counts.runs ?? 0}</strong>
                <span>运行</span>
              </div>
              <div className="check-stat">
                <strong>{counts.issues ?? 0}</strong>
                <span>规则问题</span>
              </div>
              <div className="check-stat">
                <strong>{counts.decisions ?? 0}</strong>
                <span>人工决定</span>
              </div>
            </div>
          </section>

          <Panel title="事件时间线" description="新的在前；每一条都能指回它的来源记录 ID">
            {events.length === 0 ? (
              <p className="inline-note">这个项目还没有运行、问题或人工决定记录。</p>
            ) : (
              <ul className="activity-list">
                {events.map((event) => (
                  <AuditRow event={event} key={`${event.kind}-${event.event_id}`} />
                ))}
              </ul>
            )}
          </Panel>
        </RemoteState>
      </div>
    </div>
  );
}

/** 单条事件。三类来源共用一套键，所以只写一次分支逻辑（只在"标签怎么标"上分支）。 */
function AuditRow({ event }: { event: AuditEvent }) {
  return (
    <li className="activity-item">
      <span className="mini-mark">{KIND_SYMBOL[event.kind] ?? "·"}</span>
      <div>
        <p>
          {KIND_LABEL[event.kind] ?? event.kind} · {event.title}
        </p>
        <small>
          {formatMoment(event.at)}
          {event.actor_id ? ` · ${event.actor_id}（${event.actor_role ?? "未知角色"}）` : ""}
          {event.artifact_id ? ` · ${event.artifact_id}` : ""}
        </small>
        {event.detail ? <small style={{ display: "block" }}>{event.detail}</small> : null}
      </div>
      {event.kind === "run" ? (
        <RunStateLabel status={event.status} />
      ) : (
        <StatusLabel tone={statusTone(event.status)}>{event.status}</StatusLabel>
      )}
    </li>
  );
}

/**
 * 事件状态 → 色调。
 *
 * 不认识的取值一律 `default` 并**原样显示**——不要映射成某个"看起来安全"的状态。
 */
function statusTone(status: string): "default" | "success" | "warning" | "danger" | "info" {
  if (status === "error" || status === "rejected") return "danger";
  if (status === "warning" || status === "submitted") return "warning";
  if (status === "approved") return "success";
  if (status === "info") return "info";
  return "default";
}

function sumCounts(counts: Record<string, number>): number {
  return Object.values(counts).reduce((total, value) => total + value, 0);
}

/** 时间展示到分钟（本地时区）。 */
function formatMoment(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const pad = (part: number) => String(part).padStart(2, "0");
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    ` ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}
